<template>
    <div class="pitch-offset-visualizer" :class="[`orientation-${orientation}`, `variant-${variant}`]">
        <div class="pitch-track" :class="[`orientation-${orientation}`, `variant-${variant}`]">
            <!-- Target marker (fixed at center) -->
            <div
                class="marker-container target-container"
                :class="`orientation-${orientation}`"
            >
                <div class="marker-label" v-if="variant === 'default'">Target</div>
                <div
                    class="marker-indicator target"
                    :class="[`orientation-${orientation}`, `variant-${variant}`]"
                ></div>
            </div>

            <!-- Detected marker (moves based on cents offset) -->
            <div
                class="marker-container detected-container"
                :class="[`orientation-${orientation}`, { show: showDetected }]"
                :style="markerTransform"
            >
                <div
                    class="marker-indicator detected"
                    :class="[`orientation-${orientation}`, `variant-${variant}`]"
                ></div>
                <div class="marker-label" v-if="variant === 'default'">You</div>
            </div>
        </div>

        <!-- Cent offset display -->
        <div v-if="showCents" class="offset-value">
            {{ formatCents(centsOff) }}
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed } from "vue";

interface Props {
    centsOff: number;
    maxDisplayCents?: number;
    orientation?: "horizontal" | "vertical";
    showDetected?: boolean;
    showCents?: boolean;
    variant?: "default" | "minimal";
}

const props = withDefaults(defineProps<Props>(), {
    maxDisplayCents: 50,
    orientation: "horizontal",
    showDetected: true,
    showCents: false,
    variant: "default",
});

// Convert cents to pixels for visual display
// Map -maxDisplayCents to +maxDisplayCents => -60px to +60px
const offsetPixels = computed(() => {
    const clampedCents = Math.max(
        -props.maxDisplayCents,
        Math.min(props.maxDisplayCents, props.centsOff),
    );
    return (clampedCents / props.maxDisplayCents) * 60;
});

// Transform style based on orientation
const markerTransform = computed(() => {
    if (props.orientation === "vertical") {
        // Vertical: positive cents = higher = move up = negative Y
        return { transform: `translateY(${-offsetPixels.value}px)` };
    } else {
        // Horizontal: positive cents = sharper = move right = positive X
        return { transform: `translateX(${offsetPixels.value}px)` };
    }
});

function formatCents(cents: number): string {
    const sign = cents > 0 ? "+" : "";
    return `${sign}${cents.toFixed(0)}¢`;
}
</script>

<style scoped>
.pitch-offset-visualizer {
    display: flex;
    flex-direction: column;
    gap: 8px;
    align-items: center;
}

.pitch-track {
    position: relative;
    border-radius: 8px;
    border: 1px solid rgba(0, 0, 0, 0.1);
    display: flex;
    align-items: center;
    justify-content: center;
}

.pitch-track.orientation-horizontal {
    width: 160px;
    height: 3em;
    background: linear-gradient(
        to right,
        rgba(220, 53, 69, 0.1) 0%,
        rgba(255, 193, 7, 0.1) 25%,
        rgba(40, 167, 69, 0.15) 45%,
        rgba(40, 167, 69, 0.15) 55%,
        rgba(255, 193, 7, 0.1) 75%,
        rgba(220, 53, 69, 0.1) 100%
    );
    margin-top: 1em;
    margin-bottom: 1em;
}

.pitch-track.orientation-vertical {
    width: 3em;
    height: 160px;
    background: linear-gradient(
        to bottom,
        rgba(220, 53, 69, 0.1) 0%,
        rgba(255, 193, 7, 0.1) 25%,
        rgba(40, 167, 69, 0.15) 45%,
        rgba(40, 167, 69, 0.15) 55%,
        rgba(255, 193, 7, 0.1) 75%,
        rgba(220, 53, 69, 0.1) 100%
    );
}

.marker-container {
    position: absolute;
}

.marker-container.target-container {
    z-index: 1;
}

.marker-container.detected-container {
    z-index: 2;
    transition:
        transform 0.15s ease-out,
        opacity 0.3s ease-in-out;
    opacity: 0;
}

.marker-container.detected-container.show {
    opacity: 1;
}

.marker-indicator {
    position: absolute;
    border-radius: 2px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

/* Center the bars within their containers */
.marker-indicator.orientation-horizontal {
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
}

.marker-indicator.orientation-vertical {
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
}

.marker-label {
    position: absolute;
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #495057;
    white-space: nowrap;
}

/* Horizontal orientation label positioning */
.target-container.orientation-horizontal .marker-label {
    left: 50%;
    bottom: 100%;
    transform: translateX(-50%);
    margin-bottom: 2.5em;
}

.detected-container.orientation-horizontal .marker-label {
    left: 50%;
    top: 100%;
    transform: translateX(-50%);
    margin-top: 2.5em;
}

/* Vertical orientation label positioning */
.target-container.orientation-vertical .marker-label {
    right: 100%;
    top: 50%;
    transform: translateY(-50%);
    /*margin-right: 35px;*/
    margin-right: 3em;
}

.detected-container.orientation-vertical .marker-label {
    left: 100%;
    top: 50%;
    transform: translateY(-50%);
    margin-left: 3em;
}

.marker-indicator.orientation-horizontal {
    width: 0.25em;
    height: 2em;
}

.marker-indicator.orientation-vertical {
    width: 30px;
    height: 3px;
}

.marker-indicator.target {
    background: #007bff;
}

.marker-indicator.detected {
    background: #28a745;
}

.offset-value {
    font-size: 14px;
    font-weight: 600;
    margin-top: 0.5em;
    color: #6c757d;
}

/* Minimal variant styles */
.pitch-track.variant-minimal {
    background: rgba(0, 0, 0, 0.03) !important;
    border: 1px solid rgba(0, 0, 0, 0.15);
}

.pitch-track.variant-minimal.orientation-horizontal {
    width: 120px;
    height: 2em;
    margin-top: 0.5em;
    margin-bottom: 0.5em;
}

.pitch-track.variant-minimal.orientation-vertical {
    width: 2em;
    height: 120px;
}

.marker-indicator.variant-minimal {
    box-shadow: none !important;
    border-radius: 50% !important;
}

.marker-indicator.variant-minimal.orientation-horizontal {
    width: 8px !important;
    height: 8px !important;
}

.marker-indicator.variant-minimal.orientation-vertical {
    width: 8px !important;
    height: 8px !important;
}

.marker-indicator.variant-minimal.target {
    background: #495057 !important;
}

.marker-indicator.variant-minimal.detected {
    background: #007bff !important;
}
</style>
